import _regeneratorRuntime from "@babel/runtime/regenerator";

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { Promise.resolve(value).then(_next, _throw); } }

function _asyncToGenerator(fn) { return function () { var self = this, args = arguments; return new Promise(function (resolve, reject) { var gen = fn.apply(self, args); function _next(value) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value); } function _throw(err) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err); } _next(undefined); }); }; }

import { DEFAULT_VIDEO_CONSTRAINTS } from '../../constants';
import { useCallback, useMemo, useState } from 'react';
import { getDeviceInfo, isPermissionDenied } from '../../utils';
import Video from 'twilio-video';
export default function useLocalTracks() {
  var _useState = useState(),
      audioTrack = _useState[0],
      setAudioTrack = _useState[1];

  var _useState2 = useState(),
      videoTrack = _useState2[0],
      setVideoTrack = _useState2[1];

  var _useState3 = useState(null),
      videoProcessor = _useState3[0],
      setVideoProcessor = _useState3[1];

  var _useState4 = useState(false),
      isAcquiringLocalTracks = _useState4[0],
      setIsAcquiringLocalTracks = _useState4[1];

  var getLocalAudioTrack = useCallback(function (deviceId) {
    var options = {};

    if (deviceId) {
      options.deviceId = {
        exact: deviceId
      };
    }

    return Video.createLocalAudioTrack(options).then(function (newTrack) {
      setAudioTrack(newTrack);
      return newTrack;
    });
  }, []);
  var getLocalVideoTrack = useCallback( /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(deviceId) {
      var _yield$getDeviceInfo, videoInputDevices, hasSelectedVideoDevice, options;

      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _context.next = 2;
              return getDeviceInfo();

            case 2:
              _yield$getDeviceInfo = _context.sent;
              videoInputDevices = _yield$getDeviceInfo.videoInputDevices;
              hasSelectedVideoDevice = videoInputDevices.some(function (device) {
                return deviceId && device.deviceId === deviceId;
              }); // In the DeviceSelector and FlipCameraButton components, a new video track is created,
              // then the old track is unpublished and the new track is published. Unpublishing the old
              // track and publishing the new track at the same time sometimes causes a conflict when the
              // track name is 'camera', so here we append a timestamp to the track name to avoid the
              // conflict.

              options = _extends({}, DEFAULT_VIDEO_CONSTRAINTS, {
                name: "camera-" + Date.now()
              }, hasSelectedVideoDevice && {
                deviceId: {
                  exact: deviceId
                }
              });
              return _context.abrupt("return", Video.createLocalVideoTrack(options).then(function (newTrack) {
                setVideoTrack(newTrack);

                if (videoProcessor) {
                  // console.log('getLocalVideoTrack add videoProcessor',videoProcessor._name );
                  newTrack.addProcessor(videoProcessor);
                }

                return newTrack;
              })["catch"](function (error) {
                console.error('getLocalVideoTrack failed', error);
                throw error;
              }));

            case 7:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function (_x) {
      return _ref.apply(this, arguments);
    };
  }(), [videoProcessor]);
  var removeLocalVideoTrack = useCallback(function () {
    if (videoTrack) {
      if (videoTrack.processor) {
        // console.log('removeLocalVideoTrack remove videoProcessor',videoTrack.processor._name );
        videoTrack.removeProcessor(videoTrack.processor);
      }

      videoTrack.stop();
      setVideoTrack(undefined);
    }
  }, [videoTrack]);
  var getLocalTracks = useCallback( /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(defaultVideoDevice, defaultAudioDevice) {
      var _yield$getDeviceInfo2, audioInputDevices, videoInputDevices, hasAudioInputDevices, hasVideoInputDevices, notFoundError, hasSelectedAudioDevice, hasSelectedVideoDevice, isCameraPermissionDenied, isMicrophonePermissionDenied, notAllowedError, shouldAcquireVideo, shouldAcquireAudio, localTrackConstraints, tracks, acquiredVideoTrack, acquiredAudioTrack, audioError, videoError;

      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              if (defaultVideoDevice === void 0) {
                defaultVideoDevice = null;
              }

              if (defaultAudioDevice === void 0) {
                defaultAudioDevice = null;
              }

              _context2.next = 4;
              return getDeviceInfo();

            case 4:
              _yield$getDeviceInfo2 = _context2.sent;
              audioInputDevices = _yield$getDeviceInfo2.audioInputDevices;
              videoInputDevices = _yield$getDeviceInfo2.videoInputDevices;
              hasAudioInputDevices = _yield$getDeviceInfo2.hasAudioInputDevices;
              hasVideoInputDevices = _yield$getDeviceInfo2.hasVideoInputDevices;

              if (!(!hasAudioInputDevices && !hasVideoInputDevices)) {
                _context2.next = 13;
                break;
              }

              notFoundError = new Error('device has no video or audio input devices');
              notFoundError.name = 'NotFoundError';
              throw notFoundError;

            case 13:
              hasSelectedAudioDevice = audioInputDevices.some(function (device) {
                return defaultAudioDevice && device.deviceId === defaultAudioDevice;
              });
              hasSelectedVideoDevice = videoInputDevices.some(function (device) {
                return defaultVideoDevice && device.deviceId === defaultVideoDevice;
              }); // In Chrome, it is possible to deny permissions to only audio or only video.
              // If that has happened, then we don't want to attempt to acquire the device.

              _context2.next = 17;
              return isPermissionDenied('camera');

            case 17:
              isCameraPermissionDenied = _context2.sent;
              _context2.next = 20;
              return isPermissionDenied('microphone');

            case 20:
              isMicrophonePermissionDenied = _context2.sent;

              if (!(isCameraPermissionDenied && isMicrophonePermissionDenied)) {
                _context2.next = 25;
                break;
              }

              notAllowedError = new Error('user denied permissions for video and audio input devices');
              notAllowedError.name = 'NotAllowedError';
              throw notAllowedError;

            case 25:
              if (!isAcquiringLocalTracks) {
                _context2.next = 27;
                break;
              }

              return _context2.abrupt("return");

            case 27:
              setIsAcquiringLocalTracks(true);
              shouldAcquireVideo = hasVideoInputDevices && !isCameraPermissionDenied;
              shouldAcquireAudio = hasAudioInputDevices && !isMicrophonePermissionDenied;
              localTrackConstraints = {
                video: shouldAcquireVideo && _extends({}, DEFAULT_VIDEO_CONSTRAINTS, {
                  name: "camera-" + Date.now()
                }, hasSelectedVideoDevice && {
                  deviceId: {
                    exact: defaultVideoDevice
                  }
                }),
                audio: shouldAcquireAudio && (hasSelectedAudioDevice ? {
                  deviceId: {
                    exact: defaultAudioDevice
                  }
                } : hasAudioInputDevices)
              };
              _context2.prev = 31;
              _context2.next = 34;
              return Video.createLocalTracks(localTrackConstraints);

            case 34:
              tracks = _context2.sent;
              acquiredVideoTrack = tracks.find(function (track) {
                return track.kind === 'video';
              });
              acquiredAudioTrack = tracks.find(function (track) {
                return track.kind === 'audio';
              });
              audioError = null, videoError = null;

              if (acquiredVideoTrack) {
                if (videoProcessor) {
                  // console.log('getLocalTracks add videoProcessor', videoProcessor._name);
                  acquiredVideoTrack.addProcessor(videoProcessor);
                }

                setVideoTrack(acquiredVideoTrack);
              } else {
                if (!hasVideoInputDevices) {
                  videoError = new Error('device has no video input devices');
                  videoError.name = 'VideoNotFoundError';
                } else if (isCameraPermissionDenied) {
                  videoError = new Error('user denied permissions for video input devices');
                  videoError.name = 'VideoNotAllowedError';
                }
              }

              if (acquiredAudioTrack) {
                setAudioTrack(acquiredAudioTrack);
              } else {
                if (!hasAudioInputDevices) {
                  audioError = new Error('device has no audio input devices');
                  audioError.name = 'AudioNotFoundError';
                } else if (isMicrophonePermissionDenied) {
                  audioError = new Error('user denied permissions for audio input devices');
                  audioError.name = 'AudioNotAllowedError';
                }
              }

              return _context2.abrupt("return", {
                audio: {
                  audioTrack: acquiredAudioTrack,
                  audioError: audioError,
                  defaultAudioDevice: defaultAudioDevice,
                  hasSelectedAudioDevice: hasSelectedAudioDevice
                },
                video: {
                  videoTrack: acquiredVideoTrack,
                  videoError: videoError,
                  defaultVideoDevice: defaultVideoDevice,
                  hasSelectedVideoDevice: hasSelectedVideoDevice
                }
              });

            case 41:
              _context2.prev = 41;
              setIsAcquiringLocalTracks(false);
              return _context2.finish(41);

            case 44:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2, null, [[31,, 41, 44]]);
    }));

    return function (_x2, _x3) {
      return _ref2.apply(this, arguments);
    };
  }(), [videoProcessor, isAcquiringLocalTracks]);
  var replaceAudioInputDevice = useCallback(function (newDeviceId) {
    if (audioTrack) {
      audioTrack.restart({
        deviceId: {
          exact: newDeviceId
        }
      });
    }
  }, [audioTrack]);
  var replaceVideoInputDevice = useCallback(function (newDeviceId) {
    if (videoTrack) {
      videoTrack.restart(_extends({}, DEFAULT_VIDEO_CONSTRAINTS, {
        deviceId: {
          exact: newDeviceId
        }
      }));
    }
  }, [videoTrack]);
  var localTracks = useMemo(function () {
    return [audioTrack, videoTrack].filter(function (track) {
      return track !== undefined;
    });
  }, [audioTrack, videoTrack]);
  var addProcessorToVideoTrack = useCallback(function (processor) {
    setVideoProcessor(processor);

    if (videoTrack) {
      // console.log('addProcessorToVideoTrack add videoProcessor', processor._name);
      videoTrack.addProcessor(processor);
    }
  }, [videoTrack]);
  var deleteProcessorFromVideoTrack = useCallback(function () {
    setVideoProcessor(null);

    if (videoTrack.processor) {
      // console.log('removeLocalVideoTrack remove videoProcessor', videoTrack.processor._name);
      videoTrack.removeProcessor(videoTrack.processor);
    }
  }, [videoTrack]);
  return {
    localTracks: localTracks,
    getLocalTracks: getLocalTracks,
    getLocalVideoTrack: getLocalVideoTrack,
    getLocalAudioTrack: getLocalAudioTrack,
    isAcquiringLocalTracks: isAcquiringLocalTracks,
    removeLocalVideoTrack: removeLocalVideoTrack,
    replaceAudioInputDevice: replaceAudioInputDevice,
    replaceVideoInputDevice: replaceVideoInputDevice,
    addProcessorToVideoTrack: addProcessorToVideoTrack,
    deleteProcessorFromVideoTrack: deleteProcessorFromVideoTrack
  };
}